import { AbstractAction, action } from '@packages/table-builder/registry/Action';
import type { ActionResult, ComposeModalConfig } from '@packages/table-builder/registry/Action';
import { ActionRegistry } from '@packages/table-builder/registry/ActionRegistry';
import type { ActionExecutionContext } from '@packages/table-builder/registry/ActionRegistry';
import { formBlock } from '@packages/compose/builders';
import { CustomerDepartmentService } from '@wealthlane/api-services/wealthlane-customer-service';
import { safeApiCall } from '@packages/shared/utils';

@action
export class BuildDepartmentFormAction extends AbstractAction {
  readonly id = 'BUILD_DEPARTMENT_FORM';

  async execute(ctx: ActionExecutionContext): Promise<ActionResult> {
    const meta = ctx.meta as unknown as ComposeModalConfig | undefined;
    return {
      block: formBlock('wealthlane://forms/department', meta?.mode, {
        entityId: ctx.params?.id as string | undefined,
      }),
    };
  }
}

@action
export class GetDepartmentForEditAction extends AbstractAction {
  readonly id = 'GET_DEPARTMENT_FOR_EDIT';

  async execute(ctx: ActionExecutionContext): Promise<ActionResult> {
    const id = ctx.params?.id;
    if (!id) throw new Error('[GET_DEPARTMENT_FOR_EDIT] Missing entity ID');

    const data = await safeApiCall(() =>
      CustomerDepartmentService.getApiCustomerApiAppCustomerDepartmentCustomerDepartment(
        id as string,
      ),
    );

    return data as ActionResult;
  }
}

@action
export class ViewDepartmentAction extends AbstractAction {
  readonly id = 'VIEW_DEPARTMENT';

  execute(ctx: ActionExecutionContext) {
    const entityId = ctx.pk ?? (ctx.row?.id as string);
    ActionRegistry.emitIntent({
      type: 'OPEN_COMPOSE_MODAL',
      actionId: 'BUILD_DEPARTMENT_FORM',
      context: {
        ...ctx,
        pk: entityId,
        params: { id: entityId },
        meta: {
          title: 'View Department',
          entity: 'Department',
          size: 'md',
          semanticId: 'viewDepartmentModal',
          mode: 'view',
          actions: [{ id: 'EDIT_DEPARTMENT', intent: 'edit' }],
        },
      },
    });
  }
}

@action
export class EditDepartmentAction extends AbstractAction {
  readonly id = 'EDIT_DEPARTMENT';

  execute(ctx: ActionExecutionContext) {
    const entityId = ctx.pk ?? (ctx.row?.id as string);
    ActionRegistry.emitIntent({
      type: 'OPEN_COMPOSE_MODAL',
      actionId: 'BUILD_DEPARTMENT_FORM',
      context: {
        ...ctx,
        pk: entityId,
        params: { id: entityId },
        meta: {
          title: 'Edit Department',
          entity: 'Department',
          size: 'md',
          semanticId: 'editDepartmentModal',
          mode: 'edit',
        },
      },
    });
  }
}

@action
export class AddDepartmentAction extends AbstractAction {
  readonly id = 'ADD_DEPARTMENT';

  execute(ctx: ActionExecutionContext) {
    ActionRegistry.emitIntent({
      type: 'OPEN_COMPOSE_MODAL',
      actionId: 'BUILD_DEPARTMENT_FORM',
      context: {
        ...ctx,
        pk: '',
        params: {},
        meta: {
          title: 'Add Department',
          entity: 'Department',
          size: 'md',
          semanticId: 'addDepartmentModal',
          mode: 'add',
        },
      },
    });
  }
}

@action
export class SaveDepartmentAction extends AbstractAction {
  readonly id = 'SAVE_DEPARTMENT';

  async execute(ctx: ActionExecutionContext): Promise<ActionResult> {
    const payload = ctx.params.payload as unknown as Record<string, unknown>;

    if (ctx.mode === 'create') {
      return this.handleCreate(payload);
    }

    return this.handleUpdate(payload);
  }

  private async handleCreate(payload: Record<string, unknown>): Promise<ActionResult> {
    const result =
      await CustomerDepartmentService.postApiCustomerApiAppCustomerDepartmentCustomerDepartment(
        payload as never,
      );
    return result as ActionResult;
  }

  private async handleUpdate(payload: Record<string, unknown>): Promise<ActionResult> {
    const result =
      await CustomerDepartmentService.putApiCustomerApiAppCustomerDepartmentCustomerDepartment(
        payload as never,
      );
    return result as ActionResult;
  }
}
